import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BalanceBoxes from "./BalanceBoxes";
import classes from "./Modal.module.css";
import CustomInputWithUnit from "./CustomInputWithUnit";
import { onlyNums } from "../utils/OnlyNums";
import useLanguage from "../utils/useLanguage";
import { toArabicWord } from "number-to-arabic-words/dist/index-node.js";
import { toWords } from "number-to-words";
import { sanitizeNumber } from "../utils/sanitizeNumber";
import { toMoney } from "../utils/toMoney";

const DiscountSection = ({
  originalPrice,
  finalPrice,
  onPriceChange,
  initialDiscountType = "percentage",
  initialDiscountAmount = 0,
  inputMaxWidth,
  selectedCurrency = "IQD",
  setParentDiscountType = null,
  setParentDiscountAmount = null,
}) => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");

  // discount related states
  const [discountType, setDiscountType] = useState(initialDiscountType);
  const [discountValue, setDiscountValue] = useState(
    parseFloat(sanitizeNumber(initialDiscountAmount))
  );
  const [netPrice, setNetPrice] = useState(finalPrice || originalPrice);

  // Calculate net price based on discount type and value
  const calculateNetPrice = (original, type, value) => {
    const sanitizeOriginal = parseFloat(sanitizeNumber(original));
    const sanitizedDiscountValue = sanitizeNumber(value);

    if (!sanitizedDiscountValue || sanitizedDiscountValue <= 0) return original;

    if (type === "percentage") {
      if (sanitizedDiscountValue <= 100) {
        const discountAmount = (
          (sanitizeOriginal * sanitizedDiscountValue) /
          100
        ).toFixed(2);
        return toMoney(sanitizeOriginal - discountAmount);
      }
      return toMoney(sanitizeOriginal);
    } else {
      return toMoney(Math.max(sanitizeOriginal - sanitizedDiscountValue, 0));
    }
  };

  const finatAmountInWords =
    currentDirection === "rtl"
      ? toArabicWord(netPrice || 0)
      : toWords(netPrice || 0);

  const handleDiscountTypeChange = (type) => {
    setDiscountType(type);

    // Recalculate price with the new discount type but same value
    const recalculatedPrice = calculateNetPrice(
      originalPrice,
      type,
      discountValue
    );
    setNetPrice(recalculatedPrice);

    if (setParentDiscountType !== null) {
      setParentDiscountType(type);
    }

    // Pass updated values to parent component
    onPriceChange(recalculatedPrice, type, discountValue);
  };

  const handleDiscountChange = (e) => {
    const value = e.target?.value ?? 0;

    // Add validation for percentage type
    if (discountType === "percentage" && parseFloat(value) > 100) {
      return; // Don't update if percentage is greater than 100
    }

    // if the parent want the value in the input only.
    if (setParentDiscountAmount !== null) setParentDiscountAmount(value);

    setDiscountValue(value);

    const calculatedPrice = calculateNetPrice(
      originalPrice,
      discountType,
      value
    );
    setNetPrice(calculatedPrice);

    // Pass updated values to parent component
    onPriceChange(calculatedPrice, discountType, value);
  };

  // Effect to handle initialization and props changes
  useEffect(() => {
    setDiscountType(initialDiscountType);
    setDiscountValue(initialDiscountAmount);

    // Calculate the net price based on the discount
    const calculatedPrice = calculateNetPrice(
      originalPrice,
      initialDiscountType,
      initialDiscountAmount
    );

    setNetPrice(calculatedPrice);

    // Inform parent of initial state
    onPriceChange(calculatedPrice, initialDiscountType, initialDiscountAmount);
  }, [originalPrice, initialDiscountType, initialDiscountAmount]);

  return (
    <>
      <div className="o-column u-margin--bottom_400 o-column--gap-size_300">
        <div className="o-row o-row--vertical-align_center o-row--gap-size_400">
          <input
            type="radio"
            name="discount"
            value="percentage"
            checked={discountType === "percentage"}
            onChange={() => handleDiscountTypeChange("percentage")}
          />
          <label htmlFor="percentageDiscount">
            {t("discountByPercentage")}
          </label>
        </div>
        <div className="o-row o-row--vertical-align_center o-row--gap-size_400">
          <input
            type="radio"
            name="discount"
            value="amount"
            checked={discountType === "amount"}
            onChange={() => handleDiscountTypeChange("amount")}
          />
          <label htmlFor="amountDiscount">{t("discountByAmount")}</label>
        </div>
      </div>

      <div
        className={`${
          inputMaxWidth
            ? inputMaxWidth +
              " o-row o-row--gap-size_300 o-row--horizontal-align_center"
            : classes.inputContainer
        }`}
      >
        <span
          className={`${
            inputMaxWidth
              ? inputMaxWidth + " o-row o-row--vertical-align_center"
              : ""
          }`}
        >
          {t("discount")}:{" "}
        </span>
        <div
          className={`${
            inputMaxWidth ? inputMaxWidth : ""
          } o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
        >
          <CustomInputWithUnit
            name="discountValue"
            value={discountValue}
            onChange={handleDiscountChange}
            onKeyDown={onlyNums}
            flex={true}
            unit={discountType === "percentage" ? "%" : selectedCurrency}
            min={0}
            max={discountType === "percentage" ? 100 : originalPrice}
            type="moneyV2"
          />
        </div>
      </div>

      <div className="">
        <BalanceBoxes
          boxes={[
            {
              title: "totalAmount",
              pureValue: sanitizeNumber(originalPrice) || 0,
              currency: t(selectedCurrency || "IQD"),
            },
            {
              title: "netAmount",
              pureValue: `${sanitizeNumber(netPrice) || 0}`,
              pureValueStyles: "positive-value",
              currency: t(selectedCurrency || "IQD"),
            },
          ]}
        />
      </div>
    </>
  );
};

export default DiscountSection;
